/**
 * OAuth `state` for booking — survives OAuth redirects without relying on sessionStorage
 * (Safari mobile / in-app browsers often drop partitioned storage after returning from Google).
 */
export type BookingOAuthStateV1 = {
  v: 1;
  nonce: string;
  clubNumber?: string;
  clubId?: string;
  /** Public booking URL slug (stable); preferred when using `/your-club` paths. */
  bookingSlug?: string;
  clubName?: string;
  customerTypeId?: string;
};

/** CSRF nonce — works on http://LAN-IP dev (Safari mobile is not a secure context). */
export function generateOAuthNonce(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function isBookingOAuthReturn(search: string): boolean {
  try {
    const params = new URLSearchParams(search);
    return Boolean(params.get('code') && params.get('state'));
  } catch {
    return false;
  }
}

/** Club context embedded in OAuth `state` when sessionStorage is cleared (common on mobile Safari). */
export function oauthReturnClubAuthFieldsFromSearch(search: string): {
  clubId?: string;
  clubNumber?: string;
  bookingSlug?: string;
  clubName?: string;
  customerTypeId?: string;
} {
  const params = new URLSearchParams(search);
  if (!isBookingOAuthReturn(search)) return {};
  const parsed = parseBookingOAuthState(params.get('state'));
  if (!parsed) return {};
  return {
    ...(parsed.clubId?.trim() ? { clubId: parsed.clubId.trim() } : {}),
    ...(parsed.clubNumber?.trim() ? { clubNumber: parsed.clubNumber.trim() } : {}),
    ...(parsed.bookingSlug?.trim()
      ? { bookingSlug: parsed.bookingSlug.trim().toLowerCase() }
      : {}),
    ...(parsed.clubName?.trim() ? { clubName: parsed.clubName.trim() } : {}),
    ...(parsed.customerTypeId?.trim() ? { customerTypeId: parsed.customerTypeId.trim() } : {}),
  };
}

function utf8ToBase64Url(json: string): string {
  const bytes = new TextEncoder().encode(json);
  let bin = '';
  bytes.forEach((b) => {
    bin += String.fromCharCode(b);
  });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToUtf8(b64url: string): string | null {
  try {
    const padded = b64url.replace(/-/g, '+').replace(/_/g, '/');
    const padLen = (4 - (padded.length % 4)) % 4;
    const b64 = padded + '='.repeat(padLen);
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

export function buildBookingOAuthState(fields: {
  nonce: string;
  clubNumber?: string;
  clubId?: string;
  bookingSlug?: string;
  clubName?: string;
  customerTypeId?: string;
}): string {
  const payload: BookingOAuthStateV1 = {
    v: 1,
    nonce: fields.nonce,
    ...(fields.clubNumber?.trim() ? { clubNumber: fields.clubNumber.trim() } : {}),
    ...(fields.clubId?.trim() ? { clubId: fields.clubId.trim() } : {}),
    ...(fields.bookingSlug?.trim() ? { bookingSlug: fields.bookingSlug.trim().toLowerCase() } : {}),
    ...(fields.clubName?.trim() ? { clubName: fields.clubName.trim() } : {}),
    ...(fields.customerTypeId?.trim() ? { customerTypeId: fields.customerTypeId.trim() } : {}),
  };
  return utf8ToBase64Url(JSON.stringify(payload));
}

/** Returns structured payload, or `null` if legacy random UUID-only state or malformed. */
export function parseBookingOAuthState(raw: string | null | undefined): BookingOAuthStateV1 | null {
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s) return null;
  // Legacy: bare nonce UUID returned unchanged from providers that echoed simple state.
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s)) {
    return null;
  }
  const json = base64UrlToUtf8(s);
  if (!json) return null;
  try {
    const o = JSON.parse(json) as BookingOAuthStateV1;
    if (o?.v !== 1 || typeof o.nonce !== 'string' || !o.nonce.trim()) return null;
    return o;
  } catch {
    return null;
  }
}
